/* eslint-disable no-empty */

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  listAllPropertiesAdmin,
  getPropertyByIdAdmin,
  upsertProperty,
  deletePropertyById,
  duplicateProperty,
  upsertUnitType,
  deleteUnitType,
  uploadImage,
  bookingLink,
  slugify,
  statusLabel,
} from "@/lib/properties";

const PINE = "#14231B";
const STONE = "#F8F5EF";
const LARCH = "#E3B268";
const INK = "#201E1A";

/* Robots meta = noindex when /admin mounts, restored on unmount */
const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);
};

export default function Admin() {
  useNoIndex();
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) return <FullPage>Loading…</FullPage>;
  if (!session) return <LoginScreen />;
  return <AdminShell session={session} />;
}

/* ---------- LOGIN ---------- */
const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    setBusy(false);
    if (error) setErr(error.message);
  };
  return (
    <FullPage>
      <form
        onSubmit={submit}
        className="w-full max-w-[380px] rounded-3xl p-8"
        style={{ backgroundColor: STONE, color: INK }}
      >
        <p className="label-caps text-[10px] opacity-60">ROAM Admin</p>
        <h1 className="font-display mt-3" style={{ fontSize: 34, lineHeight: 1 }}>
          Sign in.
        </h1>
        <input
          className="mt-6 w-full border-b bg-transparent py-3 text-[15px] outline-none"
          style={{ borderColor: "rgba(32,30,26,0.2)" }}
          type="email"
          placeholder="admin email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email"
          required
        />
        <input
          className="mt-4 w-full border-b bg-transparent py-3 text-[15px] outline-none"
          style={{ borderColor: "rgba(32,30,26,0.2)" }}
          type="password"
          placeholder="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="admin-password"
          required
        />
        {err && <p className="mt-3 text-[13px]" style={{ color: "#B04A2E" }}>{err}</p>}
        <button
          type="submit"
          disabled={busy}
          className="pill pill-gold mt-6 w-full"
          data-testid="admin-signin"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="mt-6 text-[12px] opacity-55">
          Access is limited. Contact ROAM if you need an admin account.
        </p>
      </form>
    </FullPage>
  );
};

const FullPage = ({ children }) => (
  <main
    className="grid min-h-screen place-items-center px-6"
    style={{ backgroundColor: PINE, color: STONE }}
  >
    {children}
  </main>
);

/* ---------- ADMIN SHELL ---------- */
const AdminShell = ({ session }) => {
  const [view, setView] = useState({ name: "list" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAllPropertiesAdmin();
      setItems(data);
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const signOut = () => supabase.auth.signOut();

  return (
    <div style={{ backgroundColor: PINE, minHeight: "100vh", color: STONE }}>
      <header
        className="flex items-center justify-between border-b px-6 py-4"
        style={{ borderColor: "rgba(237,233,224,0.15)" }}
      >
        <div className="flex items-center gap-4">
          <span className="font-display" style={{ fontSize: 22 }}>
            ROAM · Admin
          </span>
          {view.name !== "list" && (
            <button
              onClick={() => {
                setView({ name: "list" });
                load();
              }}
              className="label-caps text-[11px] opacity-70"
              data-testid="admin-back"
            >
              ← All properties
            </button>
          )}
        </div>
        <div className="flex items-center gap-4 text-[12px] opacity-70">
          <span>{session.user.email}</span>
          <button onClick={signOut} className="underline" data-testid="admin-signout">
            Sign out
          </button>
        </div>
      </header>

      {msg && (
        <div
          className="px-6 py-2 text-[13px]"
          style={{
            backgroundColor: msg.type === "err" ? "#B04A2E" : "#2E6E4A",
            color: STONE,
          }}
        >
          {msg.text}
        </div>
      )}

      <div className="p-6 md:p-10">
        {view.name === "list" && (
          <PropertyList
            items={items}
            loading={loading}
            onNew={() => setView({ name: "editor", id: null })}
            onEdit={(id) => setView({ name: "editor", id })}
            onDuplicate={async (p) => {
              try {
                await duplicateProperty(p);
                setMsg({ type: "ok", text: "Duplicated." });
                load();
              } catch (e) {
                setMsg({ type: "err", text: e.message });
              }
            }}
            onDelete={async (id) => {
              if (!window.confirm("Delete this property (and all its unit types)?"))
                return;
              try {
                await deletePropertyById(id);
                setMsg({ type: "ok", text: "Deleted." });
                load();
              } catch (e) {
                setMsg({ type: "err", text: e.message });
              }
            }}
          />
        )}
        {view.name === "editor" && (
          <PropertyEditor
            id={view.id}
            onDone={() => {
              setView({ name: "list" });
              load();
            }}
            setMsg={setMsg}
          />
        )}
      </div>
    </div>
  );
};

/* ---------- PROPERTY LIST ---------- */
const PropertyList = ({ items, loading, onNew, onEdit, onDuplicate, onDelete }) => (
  <>
    <div className="flex items-center justify-between">
      <h2 className="font-display" style={{ fontSize: 36 }}>
        Properties
      </h2>
      <button
        onClick={onNew}
        className="pill pill-gold text-[13px]"
        data-testid="admin-new-property"
      >
        + New property
      </button>
    </div>
    {loading ? (
      <p className="mt-6 opacity-60">Loading…</p>
    ) : items.length === 0 ? (
      <p className="mt-6 opacity-60">No properties yet.</p>
    ) : (
      <ul className="mt-8 divide-y" style={{ borderColor: "rgba(237,233,224,0.15)" }}>
        {items.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between border-t py-4"
            style={{ borderColor: "rgba(237,233,224,0.15)" }}
            data-testid={`admin-row-${p.slug}`}
          >
            <div>
              <p className="font-display" style={{ fontSize: 22 }}>
                {p.name}
              </p>
              <p className="text-[12px] opacity-60">
                /{p.slug} · {p.city || "—"} · {p.community || "—"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className="label-caps rounded-full px-3 py-1 text-[10px]"
                style={{
                  backgroundColor:
                    p.status === "now_leasing"
                      ? LARCH
                      : "rgba(237,233,224,0.14)",
                  color: p.status === "now_leasing" ? PINE : STONE,
                }}
              >
                {statusLabel(p.status)}
              </span>
              <button onClick={() => onEdit(p.id)} className="text-[13px] underline">
                Edit
              </button>
              <button
                onClick={() => onDuplicate(p)}
                className="text-[13px] underline opacity-70"
              >
                Duplicate
              </button>
              <button
                onClick={() => onDelete(p.id)}
                className="text-[13px] underline"
                style={{ color: "#E27A6A" }}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </>
);

/* ---------- PROPERTY EDITOR ---------- */
const emptyProp = {
  slug: "",
  name: "",
  tagline: "",
  status: "draft",
  address: "",
  city: "",
  community: "",
  community_slug: "",
  lat: null,
  lng: null,
  hero_images: [],
  video_url: "",
  neighbourhood_blurb: "",
  faq: [],
  available_from: null,
  pet_policy: "",
  smoking_policy: "",
  featured: false,
};

const PropertyEditor = ({ id, onDone, setMsg }) => {
  const [p, setP] = useState({ ...emptyProp });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    getPropertyByIdAdmin(id).then(setP);
  }, [id]);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const setNumOrNull = (k, v) => set(k, v === "" ? null : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...p };
      delete clean.unit_types;
      delete clean.hero_images_resolved;
      if (!clean.slug) clean.slug = slugify(clean.name);
      const saved = await upsertProperty(clean);
      setMsg({ type: "ok", text: "Saved." });
      set("id", saved.id);
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <h2 className="font-display" style={{ fontSize: 30 }}>
          {p.id ? "Edit property" : "New property"}
        </h2>

        <FieldRow>
          <TextField label="Name" value={p.name} onChange={(v) => set("name", v)} />
          <TextField
            label="Slug"
            value={p.slug}
            onChange={(v) => set("slug", slugify(v))}
            placeholder={slugify(p.name) || "auto"}
          />
        </FieldRow>
        <TextField label="Tagline" value={p.tagline} onChange={(v) => set("tagline", v)} />

        <FieldRow>
          <SelectField
            label="Status"
            value={p.status}
            onChange={(v) => set("status", v)}
            options={[
              ["draft", "Draft"],
              ["coming_soon", "Coming soon"],
              ["now_leasing", "Now leasing"],
              ["fully_leased", "Fully leased · Waitlist"],
            ]}
          />
          <SelectField
            label="City"
            value={p.city || ""}
            onChange={(v) => set("city", v || null)}
            options={[
              ["", "—"],
              ["Calgary", "Calgary"],
              ["Edmonton", "Edmonton"],
            ]}
          />
          <div className="flex items-center gap-2">
            <input
              id="feat"
              type="checkbox"
              checked={!!p.featured}
              onChange={(e) => set("featured", e.target.checked)}
            />
            <label htmlFor="feat" className="text-[13px]">Featured</label>
          </div>
        </FieldRow>

        <FieldRow>
          <TextField label="Address" value={p.address} onChange={(v) => set("address", v)} />
          <TextField
            label="Community"
            value={p.community}
            onChange={(v) => set("community", v)}
          />
          <TextField
            label="Community slug"
            value={p.community_slug || ""}
            onChange={(v) => set("community_slug", v)}
            placeholder="e.g. mill-woods"
          />
        </FieldRow>

        <FieldRow>
          <TextField
            label="Lat"
            value={p.lat ?? ""}
            onChange={(v) => setNumOrNull("lat", v)}
          />
          <TextField
            label="Lng"
            value={p.lng ?? ""}
            onChange={(v) => setNumOrNull("lng", v)}
          />
          <TextField
            label="Available from"
            type="date"
            value={p.available_from || ""}
            onChange={(v) => set("available_from", v || null)}
          />
        </FieldRow>

        <TextArea
          label="Neighbourhood blurb"
          value={p.neighbourhood_blurb}
          onChange={(v) => set("neighbourhood_blurb", v)}
        />
        <FieldRow>
          <TextField
            label="Pet policy"
            value={p.pet_policy}
            onChange={(v) => set("pet_policy", v)}
          />
          <TextField
            label="Smoking policy"
            value={p.smoking_policy}
            onChange={(v) => set("smoking_policy", v)}
          />
          <TextField
            label="Video URL (embed)"
            value={p.video_url}
            onChange={(v) => set("video_url", v)}
          />
        </FieldRow>

        <ImageZone
          label="Hero gallery"
          images={p.hero_images}
          onChange={(arr) => set("hero_images", arr)}
          folder={`properties/${p.slug || "new"}/hero`}
          setMsg={setMsg}
        />

        <FaqEditor
          items={p.faq || []}
          onChange={(arr) => set("faq", arr)}
        />

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={busy}
            className="pill pill-gold text-[13px]"
            data-testid="admin-save-property"
          >
            {busy ? "Saving…" : "Save property"}
          </button>
          <button onClick={onDone} className="text-[13px] underline opacity-70">
            Done
          </button>
        </div>

        {p.id && (
          <UnitTypesEditor property={p} onChange={(nu) => set("unit_types", nu)} setMsg={setMsg} />
        )}

        {p.id && p.slug && <BookingLinks propertySlug={p.slug} units={p.unit_types || []} />}
      </div>

      <aside className="text-[13px] opacity-80">
        <p className="label-caps text-[10px] opacity-60">Preview URL</p>
        <p className="mt-2 break-all">
          {p.slug ? `/residences/${p.slug}` : "—"}
        </p>
        <hr className="my-6 opacity-20" />
        <p className="opacity-70">
          <strong>Draft</strong> = hidden from public site. <br />
          <strong>Fully leased</strong> = page live but CTAs switch to
          &ldquo;Join the waitlist&rdquo;.
        </p>
      </aside>
    </div>
  );
};

/* ---------- FIELD HELPERS ---------- */
const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">{children}</div>
);
const TextField = ({ label, value, onChange, type = "text", placeholder }) => (
  <label className="block">
    <span className="label-caps text-[10px] opacity-60">{label}</span>
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full border-b bg-transparent py-2 text-[14px] outline-none"
      style={{ borderColor: "rgba(237,233,224,0.25)", color: STONE }}
    />
  </label>
);
const TextArea = ({ label, value, onChange }) => (
  <label className="block">
    <span className="label-caps text-[10px] opacity-60">{label}</span>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      className="mt-2 w-full rounded-lg bg-transparent p-3 text-[14px] outline-none"
      style={{ border: "1px solid rgba(237,233,224,0.2)", color: STONE }}
    />
  </label>
);
const SelectField = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="label-caps text-[10px] opacity-60">{label}</span>
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full border-b bg-transparent py-2 text-[14px] outline-none"
      style={{ borderColor: "rgba(237,233,224,0.25)", color: STONE }}
    >
      {options.map(([v, l]) => (
        <option key={v} value={v} style={{ color: INK }}>
          {l}
        </option>
      ))}
    </select>
  </label>
);

/* ---------- IMAGE UPLOAD ZONE ---------- */
const ImageZone = ({ label, images = [], onChange, folder, setMsg }) => {
  const [uploading, setUploading] = useState(false);
  const onFiles = async (fileList) => {
    setUploading(true);
    try {
      const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
      const paths = [];
      for (const f of files) {
        const p = await uploadImage(f, folder);
        paths.push(p);
      }
      onChange([...(images || []), ...paths]);
      setMsg?.({ type: "ok", text: `Uploaded ${paths.length} image(s).` });
    } catch (e) {
      setMsg?.({ type: "err", text: e.message });
    } finally {
      setUploading(false);
    }
  };
  const removeAt = (idx) => {
    const copy = [...images];
    copy.splice(idx, 1);
    onChange(copy);
  };
  const setCover = (idx) => {
    const copy = [...images];
    const [pick] = copy.splice(idx, 1);
    onChange([pick, ...copy]);
  };
  const moveLeft = (idx) => {
    if (idx === 0) return;
    const copy = [...images];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    onChange(copy);
  };
  const moveRight = (idx) => {
    if (idx >= images.length - 1) return;
    const copy = [...images];
    [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
    onChange(copy);
  };
  return (
    <div>
      <p className="label-caps text-[10px] opacity-60">{label}</p>
      <div
        className="mt-3 rounded-2xl border-2 border-dashed p-4"
        style={{ borderColor: "rgba(237,233,224,0.2)" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onFiles(e.dataTransfer.files);
        }}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => onFiles(e.target.files)}
          data-testid={`upload-${label.replace(/\s+/g, "-").toLowerCase()}`}
        />
        <p className="mt-2 text-[12px] opacity-60">
          Drag & drop images here or click to upload. Auto-compressed to ~1920px / 500KB.
        </p>
      </div>
      {uploading && <p className="mt-2 text-[12px] opacity-70">Uploading…</p>}
      {images.length > 0 && (
        <ul className="mt-4 grid grid-cols-3 gap-3 md:grid-cols-5">
          {images.map((path, i) => (
            <li
              key={path}
              className="relative overflow-hidden rounded-lg"
              style={{ aspectRatio: "4/3", backgroundColor: "rgba(255,255,255,0.05)" }}
            >
              <img
                src={supabasePublicUrl(path)}
                alt=""
                className="h-full w-full object-cover"
              />
              {i === 0 && (
                <span
                  className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px]"
                  style={{ backgroundColor: LARCH, color: PINE }}
                >
                  COVER
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 px-1.5 py-1 text-[10px]">
                <button onClick={() => moveLeft(i)} title="Move left">◄</button>
                <button onClick={() => setCover(i)} title="Set cover">★</button>
                <button onClick={() => moveRight(i)} title="Move right">►</button>
                <button
                  onClick={() => removeAt(i)}
                  title="Remove"
                  style={{ color: "#E27A6A" }}
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const supabasePublicUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return supabase.storage.from("properties").getPublicUrl(path).data.publicUrl;
};

/* ---- FLOORPLAN ZONE — labeled multi-plan editor ----
 * Persists the array as a JSON string inside the existing text field
 * `floorplan_image` (no schema change). Backward-compatible: a plain
 * storage path is auto-migrated to [{image, label:"Plan", caption:""}]
 * on first save.
 */
const FloorplanZone = ({ label, raw, onChange, folder, setMsg }) => {
  const parse = (r) => {
    if (!r) return [];
    const s = String(r).trim();
    if (s.startsWith("[") || s.startsWith("{")) {
      try {
        const arr = JSON.parse(s);
        return (Array.isArray(arr) ? arr : [arr]).filter((p) => p && p.image);
      } catch {}
    }
    return [{ image: s, label: "Plan", caption: "" }];
  };
  const plans = parse(raw);
  const write = (arr) =>
    onChange(arr.length ? JSON.stringify(arr) : null);

  const [uploading, setUploading] = useState(false);
  const onFiles = async (fileList) => {
    setUploading(true);
    try {
      const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
      const added = [];
      for (const f of files) {
        const path = await uploadImage(f, folder);
        added.push({ image: path, label: `Floor ${plans.length + added.length + 1}`, caption: "" });
      }
      write([...plans, ...added]);
      setMsg?.({ type: "ok", text: `Uploaded ${added.length} plan(s).` });
    } catch (e) {
      setMsg?.({ type: "err", text: e.message });
    } finally {
      setUploading(false);
    }
  };
  const updateAt = (idx, patch) => {
    const copy = [...plans];
    copy[idx] = { ...copy[idx], ...patch };
    write(copy);
  };
  const removeAt = (idx) => write(plans.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= plans.length) return;
    const copy = [...plans];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    write(copy);
  };

  return (
    <div>
      <p className="label-caps text-[10px] opacity-60">{label}</p>
      <div
        className="mt-3 rounded-2xl border-2 border-dashed p-4"
        style={{ borderColor: "rgba(237,233,224,0.2)" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onFiles(e.dataTransfer.files);
        }}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => onFiles(e.target.files)}
        />
        <p className="mt-2 text-[12px] opacity-60">
          Drop one image per floor. Auto-compressed to ~1920px / 500KB. Each plan
          gets a Label (e.g. "Floor 2") and a Caption line shown under the plan.
        </p>
      </div>
      {uploading && <p className="mt-2 text-[12px] opacity-70">Uploading…</p>}
      {plans.length > 0 && (
        <ul className="mt-4 space-y-3">
          {plans.map((p, i) => (
            <li
              key={p.image + i}
              className="grid grid-cols-[120px_1fr_auto] items-start gap-3 rounded-lg p-2"
              style={{ border: "1px solid rgba(237,233,224,0.15)" }}
            >
              <div
                style={{
                  height: 90,
                  borderRadius: 8,
                  backgroundImage: `url(${supabasePublicUrl(p.image)})`,
                  backgroundColor: "#F8F5EF",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
              <div className="space-y-2">
                <input
                  value={p.label || ""}
                  placeholder="Label (e.g. Floor 2)"
                  onChange={(e) => updateAt(i, { label: e.target.value })}
                  className="w-full bg-transparent text-[13px] outline-none"
                  style={{ color: STONE, borderBottom: "1px solid rgba(237,233,224,0.2)" }}
                />
                <textarea
                  value={p.caption || ""}
                  placeholder="Caption — one line shown under the plan"
                  rows={2}
                  onChange={(e) => updateAt(i, { caption: e.target.value })}
                  className="w-full bg-transparent text-[12px] outline-none opacity-80"
                  style={{ color: STONE }}
                />
              </div>
              <div className="flex flex-col items-end gap-1 text-[11px]">
                <button onClick={() => move(i, -1)}>▲</button>
                <button onClick={() => move(i, +1)}>▼</button>
                <button
                  onClick={() => removeAt(i)}
                  style={{ color: "#E27A6A" }}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ---------- FAQ EDITOR ---------- */
const FaqEditor = ({ items, onChange }) => {
  const add = () => onChange([...items, { question: "", answer: "" }]);
  const setAt = (i, k, v) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [k]: v };
    onChange(copy);
  };
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="label-caps text-[10px] opacity-60">FAQ</p>
        <button onClick={add} className="text-[12px] underline opacity-80">
          + Add question
        </button>
      </div>
      <ul className="mt-3 space-y-3">
        {items.map((f, i) => (
          <li
            key={i}
            className="rounded-lg p-3"
            style={{ border: "1px solid rgba(237,233,224,0.15)" }}
          >
            <input
              value={f.question}
              onChange={(e) => setAt(i, "question", e.target.value)}
              placeholder="Question"
              className="w-full bg-transparent text-[14px] outline-none"
              style={{ color: STONE }}
            />
            <textarea
              value={f.answer}
              onChange={(e) => setAt(i, "answer", e.target.value)}
              placeholder="Answer"
              rows={2}
              className="mt-2 w-full bg-transparent text-[13px] outline-none opacity-80"
              style={{ color: STONE }}
            />
            <button
              onClick={() => removeAt(i)}
              className="mt-1 text-[11px] underline"
              style={{ color: "#E27A6A" }}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ---------- UNIT TYPES SUB-EDITOR ---------- */
const UnitTypesEditor = ({ property, setMsg }) => {
  const [units, setUnits] = useState(property.unit_types || []);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    setUnits(property.unit_types || []);
  }, [property]);

  const reload = async () => {
    const fresh = await getPropertyByIdAdmin(property.id);
    setUnits(fresh.unit_types || []);
  };

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between">
        <h3 className="font-display" style={{ fontSize: 22 }}>
          Unit types
        </h3>
        <button
          onClick={() =>
            setEditing({
              property_id: property.id,
              name: "",
              beds: 1,
              baths: 1,
              sqft: null,
              rent_from: null,
              rent_to: null,
              description: "",
              spec_chips: [],
              photos: [],
              floorplan_image: null,
              units_total: 1,
              units_available: 1,
              sort_order: units.length,
            })
          }
          className="text-[13px] underline"
        >
          + Add unit type
        </button>
      </div>
      {units.length === 0 && !editing && (
        <p className="mt-3 opacity-60 text-[13px]">No unit types yet.</p>
      )}
      <ul className="mt-4 divide-y" style={{ borderColor: "rgba(237,233,224,0.15)" }}>
        {units.map((u) => (
          <li
            key={u.id}
            className="flex items-center justify-between border-t py-3"
            style={{ borderColor: "rgba(237,233,224,0.15)" }}
          >
            <div>
              <p className="text-[14px]">{u.name}</p>
              <p className="text-[11px] opacity-60">
                {u.beds}bd · {u.baths}ba · {u.sqft} sqft · ${u.rent_from}–${u.rent_to} ·{" "}
                {u.units_available}/{u.units_total} avail
              </p>
            </div>
            <div className="flex items-center gap-3 text-[12px]">
              <button onClick={() => setEditing(u)} className="underline">
                Edit
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Delete this unit type?")) return;
                  try {
                    await deleteUnitType(u.id);
                    setMsg({ type: "ok", text: "Deleted." });
                    reload();
                  } catch (e) {
                    setMsg({ type: "err", text: e.message });
                  }
                }}
                className="underline"
                style={{ color: "#E27A6A" }}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {editing && (
        <UnitForm
          value={editing}
          onCancel={() => setEditing(null)}
          onSave={async (payload) => {
            try {
              await upsertUnitType(payload);
              setEditing(null);
              setMsg({ type: "ok", text: "Unit type saved." });
              reload();
            } catch (e) {
              setMsg({ type: "err", text: e.message });
            }
          }}
          setMsg={setMsg}
          propertySlug={property.slug}
        />
      )}
    </section>
  );
};

const UnitForm = ({ value, onSave, onCancel, setMsg, propertySlug }) => {
  const [u, setU] = useState(value);
  const set = (k, v) => setU((s) => ({ ...s, [k]: v }));
  const setNum = (k, v) => set(k, v === "" ? null : Number(v));

  return (
    <div
      className="mt-6 rounded-2xl p-5"
      style={{ border: "1px solid rgba(237,233,224,0.2)" }}
    >
      <FieldRow>
        <TextField label="Name" value={u.name} onChange={(v) => set("name", v)} />
        <TextField
          label="Subtitle (e.g. '3-Bed Upper · Floors 2–3')"
          value={u.subtitle ?? ""}
          onChange={(v) => set("subtitle", v)}
        />
        <TextField label="Beds" value={u.beds ?? ""} onChange={(v) => setNum("beds", v)} />
      </FieldRow>
      <FieldRow>
        <TextField label="Baths" value={u.baths ?? ""} onChange={(v) => setNum("baths", v)} />
        <TextField label="Sqft" value={u.sqft ?? ""} onChange={(v) => setNum("sqft", v)} />
        <TextField
          label="Rent from"
          value={u.rent_from ?? ""}
          onChange={(v) => setNum("rent_from", v)}
        />
      </FieldRow>
      <FieldRow>
        <TextField
          label="Rent to"
          value={u.rent_to ?? ""}
          onChange={(v) => setNum("rent_to", v)}
        />
        <TextField
          label="Units total"
          value={u.units_total ?? 1}
          onChange={(v) => setNum("units_total", v)}
        />
        <TextField
          label="Units available"
          value={u.units_available ?? 1}
          onChange={(v) => setNum("units_available", v)}
        />
      </FieldRow>
      <FieldRow>
        <TextField
          label="Sort order"
          value={u.sort_order ?? 0}
          onChange={(v) => setNum("sort_order", v)}
        />
      </FieldRow>
      <TextArea
        label="Description"
        value={u.description}
        onChange={(v) => set("description", v)}
      />
      <TextField
        label="Spec chips (comma-separated)"
        value={(u.spec_chips || []).join(", ")}
        onChange={(v) =>
          set(
            "spec_chips",
            v
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          )
        }
      />
      <ImageZone
        label="Photos"
        images={u.photos || []}
        onChange={(arr) => set("photos", arr)}
        folder={`properties/${propertySlug}/${slugify(u.name || "unit")}`}
        setMsg={setMsg}
      />
      <FloorplanZone
        label="Floor plans"
        raw={u.floorplan_image}
        onChange={(v) => set("floorplan_image", v)}
        folder={`properties/${propertySlug}/floorplans`}
        setMsg={setMsg}
      />
      <div className="mt-4 flex gap-3">
        <button
          onClick={() => onSave(u)}
          className="pill pill-gold text-[13px]"
          data-testid="admin-save-unit"
        >
          Save unit type
        </button>
        <button onClick={onCancel} className="text-[13px] underline opacity-70">
          Cancel
        </button>
      </div>
    </div>
  );
};

/* ---------- BOOKING LINKS ---------- */
const BookingLinks = ({ propertySlug, units }) => {
  const origin = window.location.origin;
  const rows = [];
  const sources = ["website", "rentfaster", "kijiji", "facebook", "instagram"];
  for (const u of units || []) {
    for (const s of sources) {
      rows.push({
        unit: u.name,
        source: s,
        url: origin + bookingLink({ propertySlug, unitTypeName: u.name, source: s }),
      });
    }
  }
  const copy = (url) => navigator.clipboard.writeText(url);
  return (
    <section className="mt-10">
      <h3 className="font-display" style={{ fontSize: 22 }}>
        Booking links
      </h3>
      <p className="text-[12px] opacity-60">
        Deep links for each unit type × source. Paste into RentFaster, Kijiji, etc.
      </p>
      <ul className="mt-4 space-y-2 text-[12px]">
        {rows.map((r) => (
          <li
            key={r.url}
            className="flex items-center justify-between gap-4 rounded-lg p-2"
            style={{ border: "1px solid rgba(237,233,224,0.15)" }}
          >
            <div className="min-w-0 flex-1 truncate">
              <span className="label-caps mr-2 text-[9px] opacity-60">
                {r.unit} · {r.source}
              </span>
              <span className="opacity-80">{r.url}</span>
            </div>
            <button
              onClick={() => copy(r.url)}
              className="underline opacity-80"
              data-testid={`copy-${slugify(r.unit)}-${r.source}`}
            >
              Copy
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
